import React, { useState, useEffect } from "react";
import Axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { getTeachers } from "../api/onlineLecture";
import { ApiUrlAdmin } from "../api/common/Config";
import SideBar from "../components/SideBar";
import BackButton from "../components/Base/BackButton";
import Loader from "../components/Loader";
import { Components } from "../assets/Dimensions";
import ResponseStatus from "../util/enums";
import StyledDialog from "./OnlineLecture/components/StyledDialog";
import ConfirmDialog from "./OnlineLecture/components/ConfirmDialog";
import ThumbnailField from "./OnlineLecture/components/ThumbnailField";
import "./OnlineLecture/onlineLecture.css";

// Add/edit form. Mounted only while open, so closing discards the transient
// state (no stale thumbnail preview on reopen). Field rules match legacy:
// png-only image input, no required constraints.
const TeacherFormModal = ({ open, onClose, teacher, isUpdate, onSubmit }) => {
    const [name, setName] = useState(isUpdate ? teacher.TeacherName : "");
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    return (
        <StyledDialog open={open} onClose={onClose}>
            <div className="olp-theme olp-modal">
                <h2 className="olp-modal-title">{isUpdate ? "Öğretmeni Düzenle" : "Öğretmen Ekle"}</h2>
                {isUpdate && <p className="olp-modal-subtitle">{teacher.TeacherName}</p>}
                <form
                    method="post"
                    onSubmit={(event) => {
                        event.preventDefault();
                        onSubmit({ name, imageFile });
                    }}
                >
                    <ThumbnailField
                        currentUrl={isUpdate ? teacher.ImageUrl : null}
                        previewUrl={imagePreview}
                        onSelect={(file, objectUrl) => {
                            setImageFile(file);
                            setImagePreview(objectUrl);
                        }}
                    />
                    <div className="olp-field">
                        <label htmlFor="teacherName">Başlık:</label>
                        <input
                            className="olp-input"
                            type="text"
                            id="teacherName"
                            name="teacherName"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </div>
                    <div className="olp-modal-footer">
                        <button type="button" className="olp-btn" onClick={onClose}>
                            Vazgeç
                        </button>
                        <button type="submit" className="olp-btn olp-btn--primary">
                            {isUpdate ? "Öğretmeni Güncelle" : "Öğretmen Ekle"}
                        </button>
                    </div>
                </form>
            </div>
        </StyledDialog>
    );
};

// Teacher management page. Preserved legacy controls:
//  - saving is two-step: form submit only stages the data; the actual
//    addteacher/updateteacher POST fires after a confirmation dialog
//  - payload keys unchanged: teacherName, ImageUrl (file — appended even when
//    no new image was picked, matching legacy), ChannelCode "Web", Id (update)
const AddTeacher = () => {
    const userAdminToken = sessionStorage.getItem("jwtP");
    const [teacherList, setTeacherList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [formModal, setFormModal] = useState(null); // { teacher, isUpdate }
    const [pendingSave, setPendingSave] = useState(null); // { formData, name, isUpdate }

    const teachers = async () => {
        setLoading(true);
        const res = await getTeachers(userAdminToken);
        if (res.status == 200) {
            setTeacherList(res.data.ResponseData);
        }
        setLoading(false);
    };

    useEffect(() => {
        teachers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Form submit → stage the payload and open the confirmation step
    const stageSave = ({ name, imageFile }) => {
        const isUpdate = formModal.isUpdate;
        const formData = new FormData();
        formData.append("teacherName", name);
        formData.append("ImageUrl", imageFile);
        formData.append("ChannelCode", "Web");
        if (isUpdate) {
            formData.append("Id", formModal.teacher.Id);
        }
        setPendingSave({ formData, name, isUpdate });
        setFormModal(null);
    };

    // Confirmed → fire the actual request
    const saveTeacher = async () => {
        const { formData, isUpdate } = pendingSave;
        setLoading(true);
        const headers = {
            "Access-Control-Allow-Origin": "*",
            Authorization: "Bearer " + userAdminToken,
            "Content-Type": "multipart/form-data",
        };
        try {
            const res = await Axios.post(
                ApiUrlAdmin + `OnlineLecture/${isUpdate ? "updateteacher" : "addteacher"}`,
                formData,
                { headers }
            );
            if (res.status == 200) {
                if (res.data.ResponseStatus == ResponseStatus.Success) {
                    toast.success(
                        isUpdate ? "Öğretmen Başarıyla güncellenmiştir" : "Öğretmen başarıyla eklenmiştir"
                    );
                    teachers();
                } else if (res.data.ResponseStatus == ResponseStatus.Error) {
                    toast.error(res.data.ResponseMessage);
                }
            } else {
                toast.error("bir hata ile karşılaşıldı lütfen tekrar deneyiniz");
            }
        } catch (ex) {
            console.log(ex);
        }
        setLoading(false);
        setPendingSave(null);
    };

    // Newest teachers first — consistent with the lecture panel's picker
    const visibleTeachers = [...teacherList]
        .sort((a, b) => b.Id - a.Id)
        .filter((t) =>
            search
                ? t.TeacherName.toLocaleLowerCase("tr").includes(search.toLocaleLowerCase("tr"))
                : true
        );

    const containerStyle = {
        marginLeft: Components.SideBarWidth + 15,
        paddingTop: 20,
        marginRight: 30,
    };

    return (
        <div className="olp-theme olp-page">
            <SideBar />
            <ToastContainer />
            <div style={containerStyle}>
                <BackButton />
                <div className="olp-header">
                    <h1 className="olp-title">Öğretmen Yönetimi</h1>
                    <div className="olp-header-actions">
                        <button
                            className="olp-btn olp-btn--primary"
                            onClick={() => setFormModal({ teacher: null, isUpdate: false })}
                        >
                            Öğretmen ekle
                        </button>
                    </div>
                </div>
                <div className="olp-toolbar">
                    <input
                        className="olp-input olp-search"
                        placeholder="Öğretmen ara..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <span className="olp-teacher-card-id">{visibleTeachers.length} öğretmen</span>
                </div>
                <div className="olp-teacher-grid">
                    {visibleTeachers.map((item) => (
                        <div className="olp-teacher-card" key={item.Id}>
                            <img className="olp-teacher-card-img" src={item.ImageUrl} alt={item.TeacherName} />
                            <span className="olp-teacher-card-name">{item.TeacherName}</span>
                            <span className="olp-teacher-card-id">Id: {item.Id}</span>
                            <button
                                className="olp-btn olp-btn--sm"
                                onClick={() => setFormModal({ teacher: item, isUpdate: true })}
                            >
                                Öğretmeni Düzenle
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {formModal && (
                <TeacherFormModal
                    open
                    onClose={() => setFormModal(null)}
                    teacher={formModal.teacher}
                    isUpdate={formModal.isUpdate}
                    onSubmit={stageSave}
                />
            )}

            {pendingSave && (
                <ConfirmDialog
                    open
                    onClose={() => setPendingSave(null)}
                    lines={[pendingSave.name]}
                    text={
                        pendingSave.isUpdate
                            ? "Bu Öğretmeni Güncellemek istermisiniz"
                            : "Bu Öğretmeni Eklemek istermisiniz"
                    }
                    confirmLabel={pendingSave.isUpdate ? "Öğretmeni Güncelle" : "Öğretmeni Ekle"}
                    onConfirm={saveTeacher}
                />
            )}

            {loading ? <Loader /> : null}
        </div>
    );
};

export default AddTeacher;
